import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Building2, Search, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// READ-ONLY. Distinguishes Sergeant TAP employer ACCOUNTS (user-linked) from imported
// LISTING organizations (represented only for opportunity listings). No editing here.
export default function AdminOrganizations() {
  const [kind, setKind] = useState('all'); // all | account | listing
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (kind !== 'all') params.append('kind', kind);
      if (search) params.append('search', search);
      params.append('limit', '100');
      const r = await axios.get(`${BACKEND_URL}/api/admin/organizations?${params.toString()}`, { withCredentials: true });
      setData(r.data);
    } catch { setData(null); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [kind]);

  const filters = [
    { key: 'all', label: 'All' },
    { key: 'account', label: 'Employer Accounts' },
    { key: 'listing', label: 'Listing Organizations' },
  ];

  return (
    <div className="space-y-4" data-testid="admin-organizations">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-2">
          {filters.map(f => (
            <button key={f.key} data-testid={`org-filter-${f.key}`} onClick={() => setKind(f.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${kind === f.key ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-100 border border-slate-200'}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <Input data-testid="org-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchData()} placeholder="Search company name..." className="w-56 h-9 text-sm" />
          <Button size="sm" variant="outline" onClick={fetchData} data-testid="org-search-btn"><Search className="w-4 h-4" /></Button>
        </div>
      </div>

      {data && (
        <div className="flex flex-wrap gap-4 text-sm text-slate-600">
          <span><strong className="text-slate-900">{data.total}</strong> shown</span>
          <span><strong className="text-slate-900">{data.employer_accounts}</strong> employer accounts</span>
          <span><strong className="text-slate-900">{data.listing_organizations}</strong> listing organizations</span>
        </div>
      )}

      <Card className="border border-slate-200">
        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-4 text-sm font-semibold text-slate-900">Organization</th>
                  <th className="text-left p-4 text-sm font-semibold text-slate-900">Type</th>
                  <th className="text-left p-4 text-sm font-semibold text-slate-900">Status</th>
                  <th className="text-center p-4 text-sm font-semibold text-slate-900">Active Jobs</th>
                  <th className="text-center p-4 text-sm font-semibold text-slate-900">Linked Account</th>
                </tr>
              </thead>
              <tbody>
                {(data?.organizations || []).map((o) => (
                  <tr key={o.organization_id} data-testid={`org-row-${o.organization_id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-4 text-sm text-slate-900 font-medium flex items-center gap-2"><Building2 className="w-4 h-4 text-slate-400" />{o.name || '—'}</td>
                    <td className="p-4 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${o.linked_account ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>{o.type}</span>
                    </td>
                    <td className="p-4 text-sm text-slate-600">{o.status || '—'}</td>
                    <td className="p-4 text-sm text-center text-slate-700 font-medium">{o.active_jobs}</td>
                    <td className="p-4 text-sm text-center">{o.linked_account ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(!data?.organizations || data.organizations.length === 0) && (
              <div className="p-8 text-center text-slate-500">No organizations found.</div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
